import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Badge, Button, Dropdown } from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaUsers,
  FaEllipsisV,
  FaBuilding,
  FaGlobe,
  FaMapMarkerAlt,
  FaIndustry,
  FaCalendarAlt,
  FaStar,
} from "react-icons/fa";
import {
  getCompany,
  deleteCompany,
} from "../../../features/companies/companiesSlice";
import { Company } from "../../../features/companies/companiesService";
import { AppDispatch } from "../../../store";
import CompanyFormModal from "./CompanyFormModal";
import CompanyDetailModal from "./CompanyDetailModal";
import CompanyUsersModal from "./CompanyUsersModal";
import ConfirmationModal from "../../../components/common/ConfirmationModal";
import { formatDate } from "../../../utils/dateUtils";
import { Icon } from "../../../components/common/Icon";
import CompanyAvatar from "../../../components/common/CompanyAvatar";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import ClientCompaniesModal from "./CompanyClientCompaniesModal";

interface CompaniesGridProps {
  companies: Company[];
}

const CompaniesGrid: React.FC<CompaniesGridProps> = ({ companies }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [showClientCompanyModal, setShowClientCompanyModal] = useState(false);

  // Şirket detaylarını görüntüle
  const handleViewDetails = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  // Şirket düzenleme modalını aç
  const handleEdit = async (company: Company) => {
    try {
      setIsLoadingEdit(true);
      setSelectedCompany(company);

      // Önce modal'ı aç
      setShowEditModal(true);

      // Sonra company bilgilerini güncelle (opsiyonel)
      try {
        await dispatch(getCompany(company.id)).unwrap();
      } catch (apiError) {
        console.warn(
          "Could not fetch updated company data, using existing data:",
          apiError
        );
      }
    } catch (error) {
      console.error("Error in handleEdit:", error);
      setSelectedCompany(company);
      setShowEditModal(true);
    } finally {
      setIsLoadingEdit(false);
    }
  };

  // Şirket kullanıcılarını görüntüle
  const handleViewUsers = (company: Company) => {
    setSelectedCompany(company);
    setShowUsersModal(true);
  };

  // Şirket silme modalını aç
  const handleDeleteClick = (company: Company) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  // Şirketi sil
  const handleConfirmDelete = async () => {
    if (selectedCompany) {
      try {
        await dispatch(deleteCompany(selectedCompany.id)).unwrap();
      } catch (error) {
        console.error("Error deleting company:", error);
      }
    }
    setShowDeleteModal(false);
    setSelectedCompany(null);
  };

  // Modal'ları kapat
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCompany(null);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedCompany(null);
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedCompany(null);
  };

  const handleCloseClientCompanyModal = () => {
    setShowClientCompanyModal(false);
    setSelectedCompany(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedCompany(null);
  };

  // Şirket tipine göre badge rengi
  const getCompanyTypeBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case "agency":
        return (
          <Badge bg="primary" className="modern-badge">
            <Icon icon={FaBuilding} className="me-1" />
            {t("agency")}
          </Badge>
        );
      case "client":
        return (
          <Badge bg="info" className="modern-badge">
            <Icon icon={FaUsers} className="me-1" />
            {t("client")}
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="modern-badge">
            {t(type)}
          </Badge>
        );
    }
  };

  return (
    <>
      <Row className="g-4">
        {companies.map((company) => (
          <Col key={company.id} xl={4} lg={6} md={6} sm={12}>
            <Card className="modern-company-card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
              {/* Enhanced Modern Header */}
              <Card.Header className="modern-card-header border-0 p-0 position-relative">
                {/* Background Gradient */}
                <div className="header-gradient"></div>

                {/* Header Content */}
                <div className="header-content position-relative p-4">
                  <div className="d-flex align-items-start justify-content-between">
                    {/* Company Info */}
                    <div className="d-flex align-items-center flex-grow-1">
                      <div className="company-avatar-wrapper">
                        <CompanyAvatar
                          companyName={company.company_name}
                          logoUrl={company.logo_url}
                          size="md"
                          className="company-avatar"
                        />
                        <div className="company-avatar-ring"></div>
                      </div>
                      <div className="ms-4 flex-grow-1">
                        <div className="d-flex align-items-center mb-2">
                          <h5 className="company-name mb-0 text-white fw-bold text-truncate">
                            {company.company_name}
                          </h5>
                          <div className="company-verified ms-2">
                            <Icon icon={FaStar} className="text-warning" />
                          </div>
                        </div>

                        <div className="company-meta d-flex align-items-center">
                          <span className="company-id text-white-50 small me-3">
                            #{company.id.toString().padStart(4, "0")}
                          </span>
                          {company.website && (
                            <a
                              href={company.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="website-link d-flex align-items-center text-decoration-none"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Icon icon={FaGlobe} className="me-1" />
                              <span className="small">{t("website")}</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="status-badge-wrapper">
                      {company.is_active ? (
                        <div className="status-badge status-active">
                          <div className="status-dot"></div>
                          <span className="small fw-medium">{t("active")}</span>
                        </div>
                      ) : (
                        <div className="status-badge status-inactive">
                          <div className="status-dot"></div>
                          <span className="small fw-medium">
                            {t("inactive")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Type Badge */}
                  <div className="company-type-wrapper mt-3">
                    {getCompanyTypeBadge(company.company_type)}
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="header-decoration">
                  <div className="decoration-circle decoration-circle-1"></div>
                  <div className="decoration-circle decoration-circle-2"></div>
                  <div className="decoration-circle decoration-circle-3"></div>
                </div>
              </Card.Header>

              {/* Card Body */}
              <Card.Body className="p-4">
                {/* Company Info */}
                <div className="company-details">
                  {company.industry && (
                    <div className="detail-item d-flex align-items-center mb-3">
                      <div className="detail-icon">
                        <Icon icon={FaIndustry} />
                      </div>
                      <span className="detail-text">{company.industry}</span>
                    </div>
                  )}

                  {(company.city || company.country) && (
                    <div className="detail-item d-flex align-items-center mb-3">
                      <div className="detail-icon">
                        <Icon icon={FaMapMarkerAlt} />
                      </div>
                      <span className="detail-text">
                        {company.city && company.country
                          ? `${company.city}, ${company.country}`
                          : company.city || company.country}
                      </span>
                    </div>
                  )}

                  <div className="detail-item d-flex align-items-center mb-3">
                    <div className="detail-icon">
                      <Icon icon={FaCalendarAlt} />
                    </div>
                    <span className="detail-text">
                      {formatDate(company.created_at)}
                    </span>
                  </div>

                  {company.description && (
                    <div className="company-description">
                      <div className="description-label small text-muted mb-2">
                        {t("description")}
                      </div>
                      <p className="description-text">{company.description}</p>
                    </div>
                  )}
                </div>
              </Card.Body>

              {/* Card Footer */}
              <Card.Footer className="modern-card-footer border-0 p-4">
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    variant="outline-primary"
                    title={t("edit")}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      // Sadece modal gösterme işlemi yapalım
                      setSelectedCompany(company);
                      setShowEditModal(true);

                      // getCompany çağrısını tamamen kaldıralım
                    }}
                    className="action-btn flex-fill"
                  >
                    <Icon icon={FaEdit} />
                  </Button>
                  {company.company_type === "agency" && (
                    <Button
                      variant="outline-primary"
                      title={t("client_companies")}
                      className="action-btn flex-fill"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        // Sadece modal gösterme işlemi yapalım
                        setSelectedCompany(company);
                        setShowClientCompanyModal(true);

                        // getCompany çağrısını tamamen kaldıralım
                      }}
                    >
                      <Icon icon={FaBuilding} />
                    </Button>
                  )}
                  <Button
                    variant="outline-danger"
                    title={t("delete")}
                    onClick={() => handleDeleteClick(company)}
                    className="action-btn flex-fill"
                  >
                    <Icon icon={FaTrash} />
                  </Button>
                </div>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Modals - Always render but control visibility with show prop */}
      {selectedCompany && showEditModal && (
        <CompanyFormModal
          show={showEditModal}
          onHide={handleCloseEditModal}
          company={selectedCompany}
        />
      )}
      {selectedCompany && showDetailModal && (
        <CompanyDetailModal
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          company={selectedCompany}
        />
      )}

      {/* Company Users Modal */}
      {selectedCompany && showUsersModal && (
        <CompanyUsersModal
          show={showUsersModal}
          onHide={handleCloseUsersModal}
          company={selectedCompany}
        />
      )}

      {/* Client Companies Modal */}
      {selectedCompany && showClientCompanyModal && (
        <ClientCompaniesModal
          show={showClientCompanyModal}
          onHide={handleCloseClientCompanyModal}
          agencyCompany={selectedCompany}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("delete_company")}
        message={`"${selectedCompany?.company_name}" ${t("company_delete_confirmation")}`}
      />

      <style>{`
        /* Main Card Styles */
        .modern-company-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 20px !important;
          overflow: hidden;
          background: ${
            isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)"
          };
          backdrop-filter: blur(20px);
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
          } !important;
          box-shadow: ${
            isDark
              ? "0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)"
              : "0 10px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)"
          };
        }

        .modern-company-card:hover {
          transform: translateY(-12px) scale(1.02);
          box-shadow: ${
            isDark
              ? "0 25px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 25px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)"
          } !important;
        }

        .card-body {
          padding: 16px !important;
          background: transparent;
        }

        .modern-card-footer {
          padding: 16px !important;
          background: ${
            isDark ? "rgba(51, 65, 85, 0.3)" : "rgba(248, 250, 252, 0.8)"
          };
          border-top: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(226, 232, 240, 0.8)"
          };
          backdrop-filter: blur(10px);
        }

        /* Enhanced Header Styles */
        .modern-card-header {
          background: transparent;
          position: relative;
          overflow: hidden;
        }

        .header-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${
            isDark
              ? "linear-gradient(135deg, #4338ca 0%, #7c3aed 50%, #db2777 100%)"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)"
          };
          opacity: ${isDark ? "0.95" : "0.9"};
        }

        .header-content {
          position: relative;
          z-index: 2;
          padding: 16px !important;
        }

        /* Company Avatar Styles */
        .company-avatar-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .company-avatar {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          object-fit: cover;
          border: 3px solid rgba(255, 255, 255, 0.3);
          transition: all 0.3s ease;
          position: relative;
          z-index: 2;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        .company-avatar-fallback {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(15px);
          color: white;
          font-size: 1.2rem;
        }

        .company-avatar-ring {
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          animation: pulse-ring 3s infinite;
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.15);
            opacity: 0;
          }
        }

        /* Company Name and Meta */
        .company-name {
          font-size: 1.1rem;
          letter-spacing: -0.025em;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          max-width: 180px;
          color: white !important;
        }

        .company-verified {
          animation: twinkle 3s infinite;
          filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.6));
        }

        @keyframes twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.2); }
        }

        .company-id {
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
          font-weight: 600;
          background: rgba(255, 255, 255, 0.15);
          padding: 4px 8px;
          border-radius: 8px;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .website-link {
          color: rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.15);
          padding: 4px 8px;
          border-radius: 8px;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .website-link:hover {
          color: white;
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        /* Status Badge */
        .status-badge-wrapper {
          position: absolute;
          top: 12px;
          right: 12px;
        }

        .status-badge {
          display: flex;
          align-items: center;
          padding: 6px 10px;
          border-radius: 20px;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        .status-active {
          background: rgba(34, 197, 94, 0.25);
          color: #dcfce7;
        }

        .status-inactive {
          background: rgba(239, 68, 68, 0.25);
          color: #fecaca;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-right: 6px;
          animation: pulse-dot 2s infinite;
          box-shadow: 0 0 10px currentColor;
        }

        .status-active .status-dot {
          background: #22c55e;
        }

        .status-inactive .status-dot {
          background: #ef4444;
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }

        /* Company Type Badge */
        .company-type-wrapper {
          display: flex;
          justify-content: flex-start;
        }

        .modern-badge {
          background: rgba(255, 255, 255, 0.2) !important;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(15px);
          color: white !important;
          font-weight: 600;
          padding: 8px 16px;
          border-radius: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        /* Decorative Elements */
        .header-decoration {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .decoration-circle {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: float 8s ease-in-out infinite;
        }

        .decoration-circle-1 {
          width: 60px;
          height: 60px;
          top: -30px;
          right: -30px;
          animation-delay: 0s;
        }

        .decoration-circle-2 {
          width: 90px;
          height: 90px;
          bottom: -45px;
          left: -45px;
          animation-delay: 2.5s;
        }

        .decoration-circle-3 {
          width: 45px;
          height: 45px;
          top: 50%;
          right: -22px;
          animation-delay: 5s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.3; }
          25% { transform: translateY(-15px) rotate(90deg); opacity: 0.6; }
          50% { transform: translateY(0px) rotate(180deg); opacity: 0.3; }
          75% { transform: translateY(15px) rotate(270deg); opacity: 0.6; }
        }

        /* Body Styles */
        .company-details {
          margin-top: 6px;
        }

        .detail-item {
          transition: all 0.3s ease;
          padding: 8px 0;
          border-radius: 10px;
          margin-bottom: 6px !important;
        }

        .detail-item:hover {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.05)"
          };
          padding-left: 12px;
          padding-right: 12px;
          transform: translateX(4px);
        }

        .detail-icon {
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: ${
            isDark
              ? "linear-gradient(135deg, #4338ca 0%, #7c3aed 100%)"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          };
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-right: 12px;
          font-size: 0.85rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }

        .detail-item:hover .detail-icon {
          transform: scale(1.1);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
        }

        .detail-text {
          color: ${isDark ? "#e2e8f0" : "#374151"};
          font-weight: 500;
          font-size: 0.85rem;
        }

        .company-description {
          margin-top: 16px;
          padding: 16px;
          background: ${
            isDark ? "rgba(51, 65, 85, 0.4)" : "rgba(248, 250, 252, 0.8)"
          };
          border-radius: 12px;
          border-left: 4px solid ${isDark ? "#4338ca" : "#667eea"};
          backdrop-filter: blur(10px);
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
          };
        }

        .description-label {
          color: ${isDark ? "#94a3b8" : "#64748b"};
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.75rem;
        }

        .description-text {
          color: ${isDark ? "#cbd5e1" : "#334155"};
          font-size: 0.85rem;
          line-height: 1.6;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Action Buttons */
        .action-btn {
          border-radius: 12px !important;
          font-weight: 600;
          transition: all 0.3s ease;
          border-width: 2px;
          font-size: 0.85rem;
          padding: 10px 16px;
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(255, 255, 255, 0.8)"
          };
          backdrop-filter: blur(10px);
        }

        .btn-outline-primary.action-btn {
          border-color: ${
            isDark ? "rgba(59, 130, 246, 0.6)" : "rgba(59, 130, 246, 0.8)"
          };
          color: ${isDark ? "#60a5fa" : "#3b82f6"};
        }

        .btn-outline-primary.action-btn:hover {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"
          };
          border-color: #3b82f6;
          color: ${isDark ? "#93c5fd" : "#1d4ed8"};
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
        }

        .btn-outline-danger.action-btn {
          border-color: ${
            isDark ? "rgba(239, 68, 68, 0.6)" : "rgba(239, 68, 68, 0.8)"
          };
          color: ${isDark ? "#f87171" : "#ef4444"};
        }

        .btn-outline-danger.action-btn:hover {
          background: ${
            isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.1)"
          };
          border-color: #ef4444;
          color: ${isDark ? "#fca5a5" : "#dc2626"};
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .company-name {
            font-size: 1rem;
            max-width: 130px;
          }
          
          .company-avatar {
            width: 35px;
            height: 35px;
          }
          
          .status-badge-wrapper {
            position: relative;
            top: auto;
            right: auto;
            margin-top: 10px;
          }

          .header-content {
            padding: 14px !important;
          }

          .card-body {
            padding: 14px !important;
          }

          .modern-card-footer {
            padding: 14px !important;
          }

          .modern-company-card:hover {
            transform: translateY(-6px) scale(1.01);
          }
        }

        @media (max-width: 576px) {
          .d-flex.gap-2 {
            flex-direction: column;
            gap: 8px !important;
          }
          
          .action-btn {
            margin-bottom: 0;
          }
        }

               /* Dark Mode Specific Enhancements */
        ${
          isDark
            ? `
          .modern-company-card {
            background: rgba(30, 41, 59, 0.95) !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4), 
                        0 0 0 1px rgba(255, 255, 255, 0.1),
                        inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
          }

          .modern-company-card:hover {
            box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5), 
                        0 0 0 1px rgba(255, 255, 255, 0.15),
                        inset 0 1px 0 rgba(255, 255, 255, 0.15) !important;
          }

          .detail-text {
            color: #cbd5e1 !important;
          }

          .company-description {
            background: rgba(51, 65, 85, 0.6) !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
          }

          .modern-card-footer {
            background: rgba(51, 65, 85, 0.4) !important;
            border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
          }

          .action-btn {
            background: rgba(51, 65, 85, 0.7) !important;
            border: 2px solid rgba(255, 255, 255, 0.1) !important;
          }

          .btn-outline-primary.action-btn {
            color: #60a5fa !important;
            border-color: rgba(96, 165, 250, 0.5) !important;
          }

          .btn-outline-danger.action-btn {
            color: #f87171 !important;
            border-color: rgba(248, 113, 113, 0.5) !important;
          }
        `
            : `
          .modern-company-card {
            background: rgba(255, 255, 255, 0.95) !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 
                        0 0 0 1px rgba(0, 0, 0, 0.03),
                        inset 0 1px 0 rgba(255, 255, 255, 0.8) !important;
          }

          .modern-company-card:hover {
            box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15), 
                        0 0 0 1px rgba(0, 0, 0, 0.05),
                        inset 0 1px 0 rgba(255, 255, 255, 0.9) !important;
          }

          .detail-text {
            color: #374151 !important;
          }

          .company-description {
            background: rgba(248, 250, 252, 0.9) !important;
            border: 1px solid rgba(0, 0, 0, 0.05) !important;
          }

          .modern-card-footer {
            background: rgba(248, 250, 252, 0.9) !important;
            border-top: 1px solid rgba(226, 232, 240, 0.8) !important;
          }

          .action-btn {
            background: rgba(255, 255, 255, 0.9) !important;
            border: 2px solid rgba(0, 0, 0, 0.1) !important;
          }

          .btn-outline-primary.action-btn {
            color: #3b82f6 !important;
            border-color: rgba(59, 130, 246, 0.6) !important;
          }

          .btn-outline-danger.action-btn {
            color: #ef4444 !important;
            border-color: rgba(239, 68, 68, 0.6) !important;
          }
        `
        }

        /* Animation Enhancements */
        @keyframes cardEntrance {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modern-company-card {
          animation: cardEntrance 0.6s ease-out;
        }

        /* Smooth Loading Animation */
        .modern-company-card:nth-child(1) { animation-delay: 0.1s; }
        .modern-company-card:nth-child(2) { animation-delay: 0.2s; }
        .modern-company-card:nth-child(3) { animation-delay: 0.3s; }
        .modern-company-card:nth-child(4) { animation-delay: 0.4s; }
        .modern-company-card:nth-child(5) { animation-delay: 0.5s; }
        .modern-company-card:nth-child(6) { animation-delay: 0.6s; }

        /* Focus States for Accessibility */
        .action-btn:focus {
          outline: none;
          box-shadow: 0 0 0 3px ${
            isDark ? "rgba(96, 165, 250, 0.4)" : "rgba(59, 130, 246, 0.4)"
          } !important;
        }

        .website-link:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
        }

        /* Enhanced Hover Effects */
        .detail-item:hover .detail-icon {
          animation: iconBounce 0.6s ease;
        }

        @keyframes iconBounce {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.1) rotate(5deg); }
          50% { transform: scale(1.15) rotate(-5deg); }
          75% { transform: scale(1.1) rotate(5deg); }
        }

        /* Loading State */
        .company-card-loading {
          opacity: 0.7;
          pointer-events: none;
        }

        .company-card-loading .action-btn {
          opacity: 0.5;
        }

        /* Success/Error States */
        .company-card-success {
          border-color: #10b981 !important;
          box-shadow: 0 0 20px rgba(16, 185, 129, 0.3) !important;
        }

        .company-card-error {
          border-color: #ef4444 !important;
          box-shadow: 0 0 20px rgba(239, 68, 68, 0.3) !important;
        }

        /* Print Styles */
        @media print {
          .modern-company-card {
            break-inside: avoid;
            box-shadow: none !important;
            border: 1px solid #ccc !important;
          }

          .action-btn {
            display: none;
          }

          .header-decoration,
          .company-avatar-ring {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default CompaniesGrid;
